/**
 * Email-agent gate configuration, action model, and the pure `decide()` gate.
 *
 * The gate is config-driven so its behavior can change without a redeploy: the
 * agent reads the config document at the top of every `processEmail` run.
 * `decide()` is a pure function (no I/O, no clock) so it is unit-tested
 * directly and reused by both the function runtime and tests.
 *
 * The config lives at a dedicated document under the reserved `_agent` bucket
 * (see {@link getEmailAgentConfigDocumentPath}) so the whole email-agent surface
 * — emails, reports, logs, watermark, and config — sits under the one `reports`
 * tree.
 */
import {z} from 'zod';
import {
  AGENT_BUCKET_ID,
  AGENT_CONFIG_SUBCOLLECTION,
  getBucketDocPath,
} from '../reportBucket';

// ============================================================================
// Action model
// ============================================================================

/**
 * The kinds of action the agent can propose for an email.
 *
 * Only the subset in {@link AUTO_ELIGIBLE_ACTION_TYPES} can ever auto-execute;
 * the remaining, higher-consequence types (replies, forwards, deletion) always
 * route to a human regardless of configuration.
 */
export const ActionTypeSchema = z.enum([
  // Auto-eligible (low-consequence, reversible) ------------------------------
  'apply_label',
  'ack_reply',
  'create_task',
  'route',
  // Never auto-eligible (require human judgement) ----------------------------
  'send_reply',
  'forward',
  'delete',
]);

/** Inferred type for {@link ActionTypeSchema}. */
export type ActionType = z.infer<typeof ActionTypeSchema>;

/**
 * Action types that are structurally eligible for auto-execution. Types outside
 * this set can never auto-execute — {@link decide} hard-blocks them even if a
 * misconfigured allowlist includes them. This is defense-in-depth on top of the
 * allowlist, not a replacement for it.
 */
export const AUTO_ELIGIBLE_ACTION_TYPES: ReadonlySet<ActionType> = new Set([
  'apply_label',
  'ack_reply',
  'create_task',
  'route',
]);

/** Risk classification the classifier assigns to a proposed action. */
export const ActionRiskSchema = z.enum(['low', 'medium', 'high']);

/** Inferred type for {@link ActionRiskSchema}. */
export type ActionRisk = z.infer<typeof ActionRiskSchema>;

/**
 * A single action the agent proposes to take on an email. `payload` carries
 * type-specific parameters (e.g. the label to apply, the route target) and is
 * intentionally open-ended.
 */
export const ProposedActionSchema = z
  .object({
    /** The kind of action. */
    type: ActionTypeSchema,
    /** Risk classification for the action. */
    risk: ActionRiskSchema,
    /** Type-specific parameters. */
    payload: z.record(z.string(), z.unknown()).optional(),
  })
  .loose();

/** Inferred type for {@link ProposedActionSchema}. */
export type ProposedAction = z.infer<typeof ProposedActionSchema>;

// ============================================================================
// Gate config
// ============================================================================

/**
 * Editable gate configuration. Read fresh at the top of every run; a missing
 * document falls back to {@link DEFAULT_EMAIL_AGENT_CONFIG}, which ships with
 * `dryRun: true` (and is deep-frozen so it cannot be mutated) so an absent
 * config never auto-acts.
 */
export const EmailAgentConfigSchema = z
  .object({
    /**
     * Master switch. When `false`, the gate escalates everything (panic
     * button) — no email auto-acts regardless of the other fields.
     */
    enabled: z.boolean(),
    /**
     * When `true`, run live but only log what the agent *would* auto-do
     * instead of executing it. Ship in dry-run, read the logs, then flip.
     */
    dryRun: z.boolean(),
    /**
     * Minimum classifier confidence for auto-execution, in `[0, 1]`. The gate
     * compares with `>=`, so this is the inclusive lower bound.
     */
    confidenceThreshold: z.number().min(0).max(1),
    /**
     * Action types allowed to auto-execute. Intersected with
     * {@link AUTO_ELIGIBLE_ACTION_TYPES} by the gate — listing a
     * non-auto-eligible type here has no effect.
     */
    autoAllowlist: z.array(ActionTypeSchema),
    /**
     * Minimum *category* confidence for the auto-reply path, in `[0, 1]`.
     * {@link decideAutoReply} compares with `>=`, so this is the inclusive lower
     * bound (`[autoReplyConfidenceThreshold, 1]`). Independent of
     * {@link EmailAgentConfig.confidenceThreshold} (which gates the action
     * executor). Optional so an already-written config document stays readable;
     * when absent, {@link DEFAULT_AUTO_REPLY_CONFIDENCE_THRESHOLD} applies.
     */
    autoReplyConfidenceThreshold: z.number().min(0).max(1).optional(),
  })
  .loose();

/** Inferred type for {@link EmailAgentConfigSchema}. */
export type EmailAgentConfig = z.infer<typeof EmailAgentConfigSchema>;

/**
 * Fallback auto-reply category-confidence bar used when the config document
 * omits {@link EmailAgentConfig.autoReplyConfidenceThreshold}. Deliberately
 * higher than the action `confidenceThreshold` default — a wrong auto-reply is
 * outward-facing, so it demands more certainty.
 */
export const DEFAULT_AUTO_REPLY_CONFIDENCE_THRESHOLD = 0.9;

/**
 * Safe default config. Enabled but in dry-run with a conservative confidence
 * bar and only the low-consequence action types allow-listed. Used when the
 * config document is absent.
 *
 * Deep-frozen (the object AND its `autoAllowlist` array) and typed
 * `Readonly` so this shared fallback is immutable at compile time as well as
 * at runtime: a stray write to `autoAllowlist` would otherwise corrupt every
 * subsequent gate evaluation in the process.
 */
const defaultEmailAgentConfig: EmailAgentConfig = {
  enabled: true,
  dryRun: true,
  confidenceThreshold: 0.8,
  autoAllowlist: ['apply_label', 'ack_reply', 'create_task', 'route'],
  autoReplyConfidenceThreshold: DEFAULT_AUTO_REPLY_CONFIDENCE_THRESHOLD,
};
// Object.freeze is shallow, so the array needs its own freeze.
Object.freeze(defaultEmailAgentConfig.autoAllowlist);
export const DEFAULT_EMAIL_AGENT_CONFIG: Readonly<EmailAgentConfig> =
  Object.freeze(defaultEmailAgentConfig);

// ============================================================================
// Gate decision
// ============================================================================

/**
 * Outcome of the gate: `auto` means the action may execute now (subject to
 * dry-run); `escalate` means create a pending report for a human.
 */
export const GateDecisionSchema = z.enum(['auto', 'escalate']);

/** Inferred type for {@link GateDecisionSchema}. */
export type GateDecision = z.infer<typeof GateDecisionSchema>;

/**
 * The gate. Decides whether a proposed action may auto-execute or must be
 * escalated to a human. Pure — no I/O, no clock, no randomness.
 *
 * An action auto-executes only when ALL hold:
 * - the config is enabled (kill switch off);
 * - the action type is structurally auto-eligible
 *   ({@link AUTO_ELIGIBLE_ACTION_TYPES}) — hard requirement, independent of the
 *   allowlist;
 * - the action type is on the configured `autoAllowlist`;
 * - the action's risk is `low`;
 * - `confidence >= config.confidenceThreshold` (inclusive lower bound).
 *
 * Anything else escalates. Note this does NOT consider `dryRun`; dry-run
 * affects only whether an `auto` action is executed vs. logged, which is the
 * executor's concern, not the gate's.
 *
 * @param args.action - The proposed action (its `type` and `risk` are read).
 * @param args.confidence - Classifier confidence in `[0, 1]`. Values below the
 *   threshold escalate.
 * @param args.config - The gate config to apply.
 * @returns `'auto'` when the action may execute, otherwise `'escalate'`.
 */
export function decide({
  action,
  confidence,
  config,
}: {
  action: ProposedAction;
  confidence: number;
  config: EmailAgentConfig;
}): GateDecision {
  if (!config.enabled) {
    return 'escalate';
  }
  if (!AUTO_ELIGIBLE_ACTION_TYPES.has(action.type)) {
    return 'escalate';
  }
  const isAuto =
    action.risk === 'low' &&
    config.autoAllowlist.includes(action.type) &&
    confidence >= config.confidenceThreshold;
  return isAuto ? 'auto' : 'escalate';
}

// ============================================================================
// Auto-reply gate
// ============================================================================

/**
 * Outcome of the auto-reply gate: `send` means the canned acknowledgement may
 * be sent now (subject to dry-run, which the executor applies); `skip` means no
 * auto-reply. Distinct from {@link GateDecision}, which gates the action
 * executor — the two paths are independent.
 */
export const AutoReplyDecisionSchema = z.enum(['send', 'skip']);

/** Inferred type for {@link AutoReplyDecisionSchema}. */
export type AutoReplyDecision = z.infer<typeof AutoReplyDecisionSchema>;

/**
 * Case-insensitive pattern matching sender addr-specs that must never receive
 * an automatic reply: bounce/system mailboxes and no-reply senders.
 *
 * Unanchored, and matched against the whole addr-spec rather than the local
 * part, so it is deliberately broader than "mailbox is literally a bouncer":
 * `bouncer@example.edu` and any address whose DOMAIN contains `bounce` are
 * blocked too. That over-blocks a rare human sender into never receiving an
 * automated acknowledgement, which is the cheap direction to be wrong in —
 * under-blocking sends mail into a bounce loop.
 */
const AUTO_REPLY_BLOCKED_SENDER_PATTERN =
  /mailer-daemon@|postmaster@|bounce|no-?reply/i;

/**
 * `Precedence` header values (case-insensitive) that mark bulk/automated
 * traffic we must never auto-reply to — the standard anti-loop convention.
 */
const AUTO_REPLY_BLOCKED_PRECEDENCES: ReadonlySet<string> = new Set([
  'bulk',
  'junk',
  'list',
]);

/**
 * Standalone structural guard against auto-reply loops and replying to
 * automated/system senders. This is NOT admin-editable — it is a hard block
 * that no gate config or per-type opt-in can override, mirroring the
 * {@link AUTO_ELIGIBLE_ACTION_TYPES} hard block in {@link decide}. Pure, so it
 * is unit-tested directly and composed into {@link decideAutoReply}.
 *
 * Returns `true` (suppress the auto-reply) when ANY of these hold:
 * - `senderAddress` is the shared mailbox itself (`mailboxAddress`) — the
 *   reply-to-self loop guard;
 * - `senderAddress` matches {@link AUTO_REPLY_BLOCKED_SENDER_PATTERN}
 *   (mailer-daemon / postmaster / bounce / no-reply);
 * - `autoSubmitted` is present and is a non-empty value other than `no`
 *   (RFC-3834: any value other than `no` marks the message auto-submitted);
 * - `precedence` (case-insensitive, trimmed) is one of
 *   {@link AUTO_REPLY_BLOCKED_PRECEDENCES};
 * - `listId` is present and non-empty (RFC-2919: the message came from a
 *   mailing list, so a reply fans out to every subscriber);
 * - `listUnsubscribe` is present and non-empty (RFC-8058: bulk mail — the
 *   widest of the bulk signals, since many senders set it and nothing else);
 * - `returnPath` is the null envelope sender (`<>`), which marks a bounce/DSN;
 * - `xAutoreply` is present and is a non-empty value other than `no`.
 *
 * `listId`, `listUnsubscribe` and `xAutoreply` are independent of `precedence`:
 * Google Groups and most modern list software set `List-Id` without
 * `Precedence: bulk`, and bulk senders that are not lists at all often set only
 * `List-Unsubscribe`, so a precedence-only check lets both through. Reflected list replies also
 * land on fresh thread ids, so the executor's per-thread ack marker cannot stop
 * the fan-out either — this guard is the only thing that can.
 *
 * @param args.senderAddress - Bare sender addr-spec (the email's `fromAddress`),
 *   or `null` when the envelope carried none — then it is not matched on
 *   address.
 * @param args.mailboxAddress - The shared mailbox's own address, for the
 *   reply-to-self loop guard. Compared case-insensitively as a substring of
 *   `senderAddress`, which is the address a reply would actually be sent to.
 *   `null`/`undefined`/empty (an unconfigured mailbox) skips the check — there
 *   is no address to recognise ourselves by. Note a plus-addressed alias of the
 *   mailbox is NOT recognised as self.
 * @param args.autoSubmitted - RFC-3834 `Auto-Submitted` header value, or
 *   `null`/`undefined`/empty when absent — then the message is not treated as
 *   auto-submitted.
 * @param args.precedence - `Precedence` header value, or `null`/`undefined`
 *   when absent — then the message is not treated as bulk.
 * @param args.listId - RFC-2919 `List-Id` header value, or
 *   `null`/`undefined`/empty when absent — then the message is not treated as
 *   list traffic.
 * @param args.xAutoreply - `X-Autoreply` header value, or
 *   `null`/`undefined`/empty when absent — then the message is not treated as
 *   an autoresponder bounce.
 * @param args.listUnsubscribe - RFC-8058 `List-Unsubscribe` header value, or
 *   `null`/`undefined`/empty when absent — then the message is not treated as
 *   bulk. Only presence matters; the value is never parsed.
 * @param args.returnPath - `Return-Path` header value, or
 *   `null`/`undefined`/empty when absent — then the message is not treated as
 *   a bounce. Only the null envelope sender (`<>`, after trimming) blocks; an
 *   ordinary `<someone@example.com>` does not.
 * @returns `true` when auto-reply must be structurally suppressed, else `false`.
 * @throws Never — pure predicate.
 */
export function isAutoReplyStructurallyBlocked({
  senderAddress,
  mailboxAddress,
  autoSubmitted,
  precedence,
  listId,
  xAutoreply,
  listUnsubscribe,
  returnPath,
}: {
  senderAddress: string | null;
  mailboxAddress?: string | null;
  autoSubmitted?: string | null;
  precedence?: string | null;
  listId?: string | null;
  xAutoreply?: string | null;
  listUnsubscribe?: string | null;
  returnPath?: string | null;
}): boolean {
  if (isFromMailbox({senderAddress, mailboxAddress})) {
    return true;
  }
  if (
    senderAddress !== null &&
    AUTO_REPLY_BLOCKED_SENDER_PATTERN.test(senderAddress)
  ) {
    return true;
  }
  if (isPresentAndNotNo(autoSubmitted)) {
    return true;
  }
  if (
    typeof precedence === 'string' &&
    AUTO_REPLY_BLOCKED_PRECEDENCES.has(precedence.trim().toLowerCase())
  ) {
    return true;
  }
  if (typeof listId === 'string' && listId.trim().length > 0) {
    return true;
  }
  if (
    typeof listUnsubscribe === 'string' &&
    listUnsubscribe.trim().length > 0
  ) {
    return true;
  }
  if (typeof returnPath === 'string' && returnPath.trim() === '<>') {
    return true;
  }
  if (isPresentAndNotNo(xAutoreply)) {
    return true;
  }
  return false;
}

/**
 * Whether the message came from the shared mailbox itself. Kept inside this
 * module so the loop guard is computed from an address the caller already has
 * to pass, rather than from a boolean the caller decides — the executor cannot
 * get this one wrong without also getting the reply target wrong.
 *
 * @param args.senderAddress - Bare sender addr-spec, or `null` when the
 *   envelope carried none — then this returns `false` (there is also nothing
 *   to reply to).
 * @param args.mailboxAddress - The shared mailbox address, or
 *   `null`/`undefined`/empty when unconfigured — then this returns `false`.
 * @returns `true` when `mailboxAddress` occurs in `senderAddress`
 *   (case-insensitive).
 * @throws Never — pure predicate.
 */
function isFromMailbox({
  senderAddress,
  mailboxAddress,
}: {
  senderAddress: string | null;
  mailboxAddress?: string | null;
}): boolean {
  if (senderAddress === null || typeof mailboxAddress !== 'string') {
    return false;
  }
  const normalized = mailboxAddress.trim().toLowerCase();
  return (
    normalized.length > 0 && senderAddress.toLowerCase().includes(normalized)
  );
}

/**
 * Whether a loop-detection header carries a value that means "yes, this is
 * automated". Shared by the `Auto-Submitted` and `X-Autoreply` checks in
 * {@link isAutoReplyStructurallyBlocked}: both follow the RFC-3834 convention
 * that the header is only meaningful when present and non-empty, and that the
 * literal value `no` opts back out.
 *
 * @param value - The raw header value, or `null`/`undefined` when the header
 *   was absent — then this returns `false`.
 * @returns `true` when the trimmed, lowercased value is non-empty and not `no`.
 * @throws Never — pure predicate.
 */
function isPresentAndNotNo(value: string | null | undefined): boolean {
  if (typeof value !== 'string') {
    return false;
  }
  const normalized = value.trim().toLowerCase();
  return normalized.length > 0 && normalized !== 'no';
}

/**
 * The auto-reply gate. Decides whether the agent may send a canned
 * acknowledgement for a classified email. Pure — no I/O, no clock, no
 * randomness — so it is unit-tested directly and shared by the runtime.
 *
 * Returns `send` only when ALL hold:
 * - the config is enabled (same kill switch as {@link decide});
 * - the message is not structurally blocked
 *   ({@link isAutoReplyStructurallyBlocked}: the shared mailbox replying to
 *   itself, system/bounce/no-reply senders, auto-submitted mail, bulk
 *   `Precedence`, mailing-list `List-Id`, bulk `List-Unsubscribe`, a null
 *   `Return-Path`, or `X-Autoreply`) — a hard block no config can override;
 * - the inquiry type opted in (`inquiryType.autoReplyEnabled`) AND carries a
 *   non-empty `autoReplyBody` to send;
 * - the inquiry type's `risk` is `low` — a hard structural block (mirrors
 *   {@link AUTO_ELIGIBLE_ACTION_TYPES} in {@link decide}); medium/high never
 *   auto-reply even if opted in;
 * - `categoryConfidence >= threshold`, where `threshold` is
 *   `config.autoReplyConfidenceThreshold` when set, else
 *   {@link DEFAULT_AUTO_REPLY_CONFIDENCE_THRESHOLD}. The comparison is `>=`, so
 *   the accepted interval is the closed lower bound
 *   `[threshold, 1]`.
 *
 * The once-per-thread ("acknowledge a conversation once") guard is NOT enforced
 * here anymore: it moved to a bucket-independent ack marker the executor claims
 * transactionally before sending (see `maybeAutoReply` in `processEmail`), so
 * this gate stays purely about whether the message is eligible in principle.
 *
 * Anything else returns `skip`. Does NOT consider `dryRun`; dry-run affects only
 * whether a `send` decision writes a `mail/` doc vs. is logged, which is the
 * executor's concern.
 *
 * @param args.config - The gate config (reads `enabled` and
 *   `autoReplyConfidenceThreshold`).
 * @param args.inquiryType - The assigned inquiry type's auto-reply inputs
 *   (`autoReplyEnabled`, `autoReplyBody`, `risk`). `autoReplyEnabled`
 *   absent/`undefined` is treated as opted-out; `autoReplyBody` `null`/absent or
 *   whitespace-only is treated as no body.
 * @param args.categoryConfidence - Classifier confidence that the assigned
 *   category is correct, in `[0, 1]`.
 * @param args.senderAddress - Bare sender addr-spec for the structural block, or
 *   `null` when the envelope carried none.
 * @param args.mailboxAddress - The shared mailbox's own address, for the
 *   reply-to-self loop guard inside the structural block. `null`/absent (an
 *   unconfigured mailbox) skips that one check; every other block still applies.
 * @param args.autoSubmitted - RFC-3834 `Auto-Submitted` header value for the
 *   structural block, or `null`/`undefined`/absent when the header was not set.
 * @param args.precedence - `Precedence` header value for the structural block,
 *   or `null`/`undefined`/absent when the header was not set.
 * @param args.listId - RFC-2919 `List-Id` header value for the structural
 *   block, or `null`/`undefined`/absent when the header was not set.
 * @param args.xAutoreply - `X-Autoreply` header value for the structural block,
 *   or `null`/`undefined`/absent when the header was not set.
 * @param args.listUnsubscribe - RFC-8058 `List-Unsubscribe` header value for the
 *   structural block, or `null`/`undefined`/absent when the header was not set.
 * @param args.returnPath - `Return-Path` header value for the structural block,
 *   or `null`/`undefined`/absent when the header was not set.
 * @returns `'send'` when an auto-reply may be sent, otherwise `'skip'`.
 * @throws Never — pure gate.
 */
export function decideAutoReply({
  config,
  inquiryType,
  categoryConfidence,
  senderAddress,
  mailboxAddress,
  autoSubmitted,
  precedence,
  listId,
  xAutoreply,
  listUnsubscribe,
  returnPath,
}: {
  config: EmailAgentConfig;
  inquiryType: {
    autoReplyEnabled?: boolean;
    autoReplyBody?: string | null;
    risk: ActionRisk;
  };
  categoryConfidence: number;
  senderAddress: string | null;
  mailboxAddress?: string | null;
  autoSubmitted?: string | null;
  precedence?: string | null;
  listId?: string | null;
  xAutoreply?: string | null;
  listUnsubscribe?: string | null;
  returnPath?: string | null;
}): AutoReplyDecision {
  if (!config.enabled) {
    return 'skip';
  }
  if (
    isAutoReplyStructurallyBlocked({
      senderAddress,
      mailboxAddress,
      autoSubmitted,
      precedence,
      listId,
      xAutoreply,
      listUnsubscribe,
      returnPath,
    })
  ) {
    return 'skip';
  }
  if (inquiryType.risk !== 'low') {
    return 'skip';
  }
  if (inquiryType.autoReplyEnabled !== true) {
    return 'skip';
  }
  const hasBody =
    typeof inquiryType.autoReplyBody === 'string' &&
    inquiryType.autoReplyBody.trim().length > 0;
  if (!hasBody) {
    return 'skip';
  }
  const threshold =
    config.autoReplyConfidenceThreshold ??
    DEFAULT_AUTO_REPLY_CONFIDENCE_THRESHOLD;
  return categoryConfidence >= threshold ? 'send' : 'skip';
}

// ============================================================================
// Path
// ============================================================================

/** Document id of the singleton gate-config document. */
export const EMAIL_AGENT_CONFIG_DOC_ID = 'settings' as const;

/**
 * Path to the singleton gate-config document, nested under the reserved
 * `_agent` bucket's `config` subcollection.
 *
 * @returns Firestore doc path `reports/_agent/config/settings`.
 */
export function getEmailAgentConfigDocumentPath(): string {
  return `${getBucketDocPath({bucketId: AGENT_BUCKET_ID})}/${AGENT_CONFIG_SUBCOLLECTION}/${EMAIL_AGENT_CONFIG_DOC_ID}`;
}
